import { useContext, useEffect, useState } from 'react';

import { Context as CurrenciesContext } from '../api/currencies/context';
import { Context as ExchangeRatesContext } from '../api/exchangeRates/context';
import { Context as DestinationsContext } from '../api/destinations/context';
import { Context as LocationsContext } from '../api/locations/context';
import { Context as CategoriesContext } from '../api/categories/context';
import { Context as PriceLevelsContext } from '../api/priceLevels/context';
import { Context as SupplierAccountsContext } from '../api/supplierAccounts/context';
import { Context as ComponentLevelsContext } from '../api/componentLevels/context';
import { Context as IncomeTypesContext } from '../api/incomeTypes/context';
import { Context as PaymentMethodsContext } from '../api/paymentMethods/context';
import { Context as ServicePaidByOptionsContext } from '../api/servicePaidByOptions/context';
import { Context as CountriesContext } from '../api/countries/context';
import { Context as PriceComponentsContext } from '../api/priceComponents/context';
import { Context as ServiceTypesContext } from '../api/serviceTypes/context';
import { Context as ServiceAbbreviationsContext } from '../api/serviceAbbreviations/context';
import { Context as AirlinesContext } from '../api/airlines/context';
import { Context as AirportsContext } from '../api/airports/context';
import { Context as LanguagesContext } from '../api/languages/context';
import { Context as CompaniesContext } from '../api/companies/context';
import { Context as TravelAgencyAccountsContext } from '../api/travelAgencyAccounts/context';
import { Context as BankAccountsContext } from '../api/bankAccounts/context';
import { Context as AgentCommissionAccountsContext } from '../api/agentCommissionAccounts/context';
import { Context as DiscountAccountsContext } from '../api/discountAccounts/context';
import { Context as GeneralAccountsContext } from '../api/generalAccounts/context';
import { Context as IncomeAccountsContext } from '../api/incomeAccounts/context';
import { Context as AppSettingsContext } from '../api/appSettings/context';
import { Context as UserPreferencesContext } from '../api/userPreferences/context';
import { Context as UsersContext } from '../api/users/context';
import { Context as RolesContext } from '../api/roles/context';
import { Context as TravelFileAttributesContext } from '../api/travelFileAttributes/context';
import { Context as ClassesContext } from '../api/classes/context';
import MealContext from '../api/meals/context/context';
import { hasContextData } from '../utils/contextData';

// Persists across HMR in dev mode (module variables reset on HMR)
let prefetchPromise: Promise<void> | null = null;

// Shared with the screens that guard their own lookup fetches, so there is one
// definition of "already loaded" rather than two that can drift apart.
const hasData = hasContextData;

/**
 * Prefetch all static/lookup data once after login.
 * Returns `isReady` — false while fetching, true once all data is loaded.
 * Uses context data presence (not a flag) to decide, so survives HMR and route changes.
 */
export default function usePrefetchStaticData(enabled: boolean = true): boolean {
    const currenciesContext = useContext(CurrenciesContext);
    const exchangeRatesContext = useContext(ExchangeRatesContext);
    const destinationsContext = useContext(DestinationsContext);
    const locationsContext = useContext(LocationsContext);
    const categoriesContext = useContext(CategoriesContext);
    const priceLevelsContext = useContext(PriceLevelsContext);
    const supplierAccountsContext = useContext(SupplierAccountsContext);
    const componentLevelsContext = useContext(ComponentLevelsContext);
    const incomeTypesContext = useContext(IncomeTypesContext);
    const paymentMethodsContext = useContext(PaymentMethodsContext);
    const servicePaidByOptionsContext = useContext(ServicePaidByOptionsContext);
    const countriesContext = useContext(CountriesContext);
    const priceComponentsContext = useContext(PriceComponentsContext);
    const serviceTypesContext = useContext(ServiceTypesContext);
    const serviceAbbreviationsContext = useContext(ServiceAbbreviationsContext);
    const airlinesContext = useContext(AirlinesContext);
    const airportsContext = useContext(AirportsContext);
    const languagesContext = useContext(LanguagesContext);
    const companiesContext = useContext(CompaniesContext);
    const travelAgencyAccountsContext = useContext(TravelAgencyAccountsContext);
    const bankAccountsContext = useContext(BankAccountsContext);
    const agentCommissionAccountsContext = useContext(AgentCommissionAccountsContext);
    const discountAccountsContext = useContext(DiscountAccountsContext);
    const generalAccountsContext = useContext(GeneralAccountsContext);
    const incomeAccountsContext = useContext(IncomeAccountsContext);
    const mealsContext = useContext(MealContext);
    const appSettingsContext = useContext(AppSettingsContext);
    const userPreferencesContext = useContext(UserPreferencesContext);
    const usersContext = useContext(UsersContext);
    const rolesContext = useContext(RolesContext);
    const travelFileAttributesContext = useContext(TravelFileAttributesContext);
    const classesContext = useContext(ClassesContext);

    // Check if critical data is already loaded (survives HMR and route changes)
    const allContexts = [
        currenciesContext, destinationsContext, locationsContext,
        categoriesContext, supplierAccountsContext, componentLevelsContext,
        incomeTypesContext, paymentMethodsContext, countriesContext
    ];
    const alreadyLoaded = allContexts.every(hasData);

    const [isReady, setIsReady] = useState(alreadyLoaded);

    useEffect(() => {
        if (!enabled) return;

        if (alreadyLoaded) {
            setIsReady(true);
            return;
        }

        // If another mount already started the prefetch, wait for it
        if (prefetchPromise) {
            prefetchPromise.then(() => setIsReady(true));
            return;
        }

        const promises: Promise<any>[] = [];

        const fetchIfEmpty = (context: any, args: any[] = [{}]) => {
            if (!hasData(context)) {
                promises.push(context.get(...args).catch(() => {}));
            }
        };

        fetchIfEmpty(currenciesContext, []);
        fetchIfEmpty(exchangeRatesContext);
        fetchIfEmpty(destinationsContext, [{}, { projection: 'name' }]);
        fetchIfEmpty(locationsContext, [{}, { projection: 'name' }]);
        fetchIfEmpty(categoriesContext, [{}, { projection: 'name' }]);
        fetchIfEmpty(priceLevelsContext, [{}, { projection: 'name' }]);
        fetchIfEmpty(supplierAccountsContext);
        fetchIfEmpty(componentLevelsContext, [{}, { projection: 'name' }]);
        fetchIfEmpty(incomeTypesContext);
        fetchIfEmpty(paymentMethodsContext);
        fetchIfEmpty(servicePaidByOptionsContext);
        fetchIfEmpty(countriesContext, [{}, { projection: 'name nationality alpha3Code alpha2Code' }]);
        fetchIfEmpty(priceComponentsContext);
        fetchIfEmpty(serviceTypesContext);
        fetchIfEmpty(serviceAbbreviationsContext);
        fetchIfEmpty(airlinesContext, [{}, { projection: 'name code' }]);
        fetchIfEmpty(airportsContext, [{}, { projection: 'name iataCode municipality' }]);
        fetchIfEmpty(languagesContext);
        fetchIfEmpty(mealsContext);
        fetchIfEmpty(companiesContext);
        fetchIfEmpty(travelAgencyAccountsContext, [{}, { projection: 'companyName agentCommissionPercent addVatOnCommissionToTravelAgency agentCommissionIsEqualToFlightSupplierCommission' }]);
        fetchIfEmpty(bankAccountsContext, [{}, { projection: 'accountName bankName currency' }]);
        fetchIfEmpty(agentCommissionAccountsContext, [{}, { projection: 'accountName applyVatOnCommission generalLedgerAccountNumber' }]);
        fetchIfEmpty(discountAccountsContext, [{}, { projection: 'accountName' }]);
        fetchIfEmpty(generalAccountsContext, [{}, { projection: 'accountName generalLedgerAccountNumber' }]);
        fetchIfEmpty(incomeAccountsContext, [{}, { projection: 'accountName generalLedgerAccountNumber' }]);
        // No projection, deliberately. The screens that need this list call
        // `usersContext.get({})` with none either, and getDataOptions hands the
        // whole user object to its consumers — usePdfInfoBuilder among them — so
        // narrowing the fields here would quietly strip something a PDF needs.
        // The point of prefetching it is WHEN it is fetched, not what: opening
        // Find Documents used to request it alone at 4.2s, after everything else
        // had already returned.
        fetchIfEmpty(usersContext);
        // Roles too: the Users list resolves each user's role name through this
        // list, and fetching it from the page raced the grid — whichever won
        // decided whether the Role column had names or blanks.
        fetchIfEmpty(rolesContext);
        // Same race as Roles, on the Travel File List: its Attribute column turns
        // the travel file's attribute id into a name from this list, and loading
        // it from the page raced the grid — so the column showed names or bare
        // ids depending on which returned first. Classes feed that page's Class
        // filter the same way. Full rows: TourDetailsTab skips its own fetch
        // once classes are present and reads more than the name.
        fetchIfEmpty(travelFileAttributesContext, [{ isDeleted: null }]);
        fetchIfEmpty(classesContext, [{ isDeleted: null }]);

        // Fetch app settings and user preferences (simple fetch, no context data check)
        promises.push(appSettingsContext.fetch().catch(() => {}));
        promises.push(userPreferencesContext.fetch().catch(() => {}));

        if (promises.length === 0) {
            setIsReady(true);
        } else {
            prefetchPromise = Promise.all(promises).then(() => {
                prefetchPromise = null;
                setIsReady(true);
            });
        }
    }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    return isReady;
}
